/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Block } from '@sanity/block-content-to-react';
import {
  Box,
  Flex,
  Grid,
  IntrinsicImage,
  Link,
  SanityRichText,
  SxStyleProp,
  Text,
  useBreakpointIndex,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';

export interface ICard {
  callOut?: boolean;
  capacity?: number;
  cardTitle?: string;
  cardText?: string;
  cta?: {
    caption?: string;
    url?: string;
  };
  image?: Queries.SanityImageType;
  pdf?: Queries.SanityFileType;
}

interface TPrivateDiningAndVenuesGrid {
  cards: ICard[];
  showLearnMoreOnMobile?: boolean;
  hasFeatureCard?: boolean;
  desktopGridColumns?: number;
  desktopGridGap?: string;
  title?: string;
  showCta?: boolean;
  eyebrow?: string;
  showCapacity?: boolean;
  variant?: 'default' | 'private-events';
  titleSx?: SxStyleProp;
}

export const PrivateDiningAndVenuesGrid = ({
  cards,
  showLearnMoreOnMobile = false,
  hasFeatureCard = false,
  desktopGridColumns = 3,
  desktopGridGap = '24px',
  title,
  showCta = false,
  eyebrow,
  showCapacity = true,
  variant = 'default',
  titleSx = {},
}: TPrivateDiningAndVenuesGrid): JSX.Element => {
  const isMobile = useBreakpointIndex() < 2;
  const privateEventsVariant = variant === 'private-events';

  return (
    <Box>
      {eyebrow && (
        <Box sx={{ display: ['block', 'none'] }}>
          <Text
            sx={{
              fontSize: '14px',
              textAlign: 'center',
              letterSpacing: '2px',
              marginBottom: '24px',
            }}
            variant="body"
          >
            {eyebrow}
          </Text>
        </Box>
      )}
      {(title || showCta) && (
        <Flex
          sx={{
            paddingBottom: '27px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            sx={{
              alignSelf: 'stretch',
              marginTop: '-1px',
              fontSize: ['44px', '46px'],
              ...titleSx,
            }}
            variant="h2"
          >
            {title}
          </Text>
          {showCta && (
            <Box sx={{ display: ['none', 'block'] }}>
              <Link to="/private-events/all-venues/" variant="underline">
                VIEW ALL VENUES
              </Link>
            </Box>
          )}
        </Flex>
      )}
      <Grid
        columns={[1, desktopGridColumns]}
        sx={{ gap: ['32px 16px', desktopGridGap], mb: [2, 0] }}
      >
        {cards?.map((card, index) => {
          if (
            !isMobile &&
            (card.callOut === true || (hasFeatureCard && index === 0))
          ) {
            return (
              // FULL WIDTH CALLOUT CARD : Shows on desktop only
              <Box
                key={`callout-${index}`}
                sx={{
                  gridColumn: hasFeatureCard
                    ? `span ${desktopGridColumns}`
                    : 'span 2',
                  mb: '32px',
                }}
              >
                <Grid columns={1} gap="24px">
                  {hasFeatureCard && (
                    <IntrinsicImage
                      alt={card?.image?.alt ?? ''}
                      image={
                        card?.image?.asset?.gatsbyImageData as IGatsbyImageData
                      }
                      sx={{ maxHeight: 640 }}
                    />
                  )}
                  <Flex
                    sx={{
                      maxWidth: hasFeatureCard ? '50%' : '100%',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '16px',
                    }}
                  >
                    <Text
                      sx={{
                        fontSize: [5, privateEventsVariant ? 5 : 7],
                        fontWeight: 'medium',
                        lineHeight: [
                          '34px',
                          privateEventsVariant ? '34px' : '52px',
                        ],
                        variant: 'heading',
                      }}
                    >
                      {card.cardTitle}
                    </Text>
                    {/* CAPACITY & PDF */}
                    {showCapacity && (card.capacity || card?.pdf?.asset?.url) && (
                      <Flex paddingY={4}>
                        {card.capacity && (
                          <Flex>
                            <Icons.Users
                              style={{ maxHeight: 24, maxWidth: 24 }}
                            />
                            <Text
                              sx={{
                                fontSize: 2,
                                fontWeight: 'semibold',
                                lineHeight: '24px',
                                ml: '14px',
                              }}
                              variant="smallTitle"
                            >
                              Capacity:&nbsp;
                              {card.capacity}
                            </Text>
                          </Flex>
                        )}
                        {card?.pdf?.asset?.url && (
                          <Link
                            sx={{ textDecoration: 'none' }}
                            to={card?.pdf?.asset?.url ?? ''}
                          >
                            <Flex sx={{ ml: 6 }}>
                              <Icons.Download
                                style={{ maxHeight: 24, maxWidth: 24 }}
                              />
                              <Text
                                sx={{
                                  fontSize: 2,
                                  fontWeight: 'semibold',
                                  lineHeight: '24px',
                                  ml: '14px',
                                }}
                                variant="smallTitle"
                              >
                                Detail PDF
                              </Text>
                            </Flex>
                          </Link>
                        )}
                      </Flex>
                    )}

                    {typeof card.cardText === 'string' && (
                      <Text sx={{ fontSize: '16px' }} variant="mediumP">
                        {card.cardText}
                      </Text>
                    )}

                    {typeof card.cardText === 'object' && (
                      <SanityRichText
                        blocks={card.cardText as unknown as Block[]}
                        components={{
                          p: ({ children }) => (
                            <Text sx={{ fontSize: '16px' }} variant="body">
                              {children as React.ReactNode}
                            </Text>
                          ),
                        }}
                      />
                    )}
                    {/* Render a "Learn More" link if the card has a CTA URL.
                        The link is always visible on desktop, but on mobile it's only shown if showLearnMoreOnMobile is true.
                        Uses the CTA caption if provided, otherwise defaults to "Learn More" */}
                    {card?.cta?.url && (
                      <Link
                        sx={{
                          display: [
                            showLearnMoreOnMobile ? 'inline-block' : 'none',
                            'inline-block',
                          ],
                        }}
                        to={card.cta.url}
                        variant="underline"
                      >
                        {card?.cta?.caption ?? 'Learn More'}
                      </Link>
                    )}
                  </Flex>
                  {!hasFeatureCard && (
                    <IntrinsicImage
                      alt={card?.image?.alt ?? ''}
                      image={
                        card?.image?.asset?.gatsbyImageData as IGatsbyImageData
                      }
                      sx={{ maxHeight: 388 }}
                    />
                  )}
                </Grid>
              </Box>
            );
          } else {
            // 1/2 WIDTH NON-CALLOUT CARD
            return (
              <Flex
                key={index}
                sx={{
                  gridColumn: 'span 1',
                  mb: 2,
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <IntrinsicImage
                  alt={card?.image?.alt ?? ''}
                  image={
                    card?.image?.asset?.gatsbyImageData as IGatsbyImageData
                  }
                  sx={{
                    maxHeight: [228, 388],
                    mb: [privateEventsVariant ? '8px' : '40px', '8px'],
                  }}
                />
                <Text
                  sx={{
                    fontSize: [privateEventsVariant ? 5 : 6, 5],
                    fontWeight: 'medium',
                    lineHeight: [
                      privateEventsVariant ? 'inherit' : '34px',
                      'inherit',
                    ],
                    variant: 'heading',
                  }}
                >
                  {card.cardTitle}
                </Text>
                {/* CAPACITY & PDF MOBILE */}
                {showCapacity && (card.capacity || card?.pdf?.asset?.url) && (
                  <Flex
                    key={index}
                    marginBottom={2}
                    marginTop={2}
                    paddingY={1}
                    sx={{ display: ['flex', 'none'] }}
                  >
                    {card.capacity && (
                      <Flex>
                        <Icons.Users style={{ maxHeight: 24, maxWidth: 24 }} />
                        <Text
                          sx={{
                            fontSize: 2,
                            fontWeight: 'semibold',
                            lineHeight: '24px',
                            ml: '14px',
                          }}
                          variant="smallTitle"
                        >
                          Capacity:&nbsp;
                          {card.capacity}
                        </Text>
                      </Flex>
                    )}
                    {card?.pdf?.asset?.url && (
                      <Link
                        sx={{ textDecoration: 'none' }}
                        to={card?.pdf?.asset?.url ?? ''}
                      >
                        <Flex sx={{ ml: 3 }}>
                          <Icons.Download
                            style={{ maxHeight: 24, maxWidth: 24 }}
                          />
                          <Text
                            sx={{
                              fontSize: 2,
                              fontWeight: 'semibold',
                              lineHeight: '24px',
                              ml: '14px',
                            }}
                            variant="smallTitle"
                          >
                            Detail PDF
                          </Text>
                        </Flex>
                      </Link>
                    )}
                  </Flex>
                )}
                {typeof card.cardText === 'string' && (
                  <Text sx={{ fontSize: '16px', variant: 'mediumP' }}>
                    {card.cardText}
                  </Text>
                )}

                {typeof card.cardText === 'object' && (
                  <SanityRichText
                    blocks={card.cardText as unknown as Block[]}
                    components={{
                      p: ({ children }) => (
                        <Text sx={{ fontSize: '16px' }} variant="body">
                          {children as React.ReactNode}
                        </Text>
                      ),
                    }}
                    sx={{
                      my: 3,
                    }}
                  />
                )}
                <Link
                  sx={{
                    display: [
                      showLearnMoreOnMobile ? 'inline-block' : 'none',
                      'inline-block',
                    ],
                    alignSelf: 'flex-start',
                  }}
                  to={card?.cta?.url ?? ''}
                  variant="underline"
                >
                  {card?.cta?.caption ?? 'Learn More'}
                </Link>
                {/* CAPACITY & PDF DESKTOP */}
                {showCapacity && (card.capacity || card?.pdf?.asset?.url) && (
                  <Flex
                    key={`flex-${index}`}
                    marginTop={4}
                    paddingY={1}
                    sx={{ display: ['none', 'flex'] }}
                  >
                    {card.capacity && (
                      <Flex>
                        <Icons.Users style={{ maxHeight: 24, maxWidth: 24 }} />
                        <Text
                          sx={{
                            fontSize: 2,
                            fontWeight: 'semibold',
                            lineHeight: '24px',
                            ml: '14px',
                          }}
                          variant="smallTitle"
                        >
                          Capacity:&nbsp;
                          {card.capacity}
                        </Text>
                      </Flex>
                    )}

                    {card?.pdf?.asset?.url && (
                      <Link
                        sx={{ textDecoration: 'none' }}
                        to={card?.pdf?.asset?.url ?? ''}
                      >
                        <Flex sx={{ ml: 3 }}>
                          <Icons.Download
                            style={{ maxHeight: 24, maxWidth: 24 }}
                          />
                          <Text
                            sx={{
                              fontSize: 2,
                              fontWeight: 'semibold',
                              lineHeight: '24px',
                              ml: '14px',
                            }}
                            variant="smallTitle"
                          >
                            Detail PDF
                          </Text>
                        </Flex>
                      </Link>
                    )}
                  </Flex>
                )}
              </Flex>
            );
          }
        })}
      </Grid>
    </Box>
  );
};
